import React, { useState } from 'react';
import { Outlet, NavLink, useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import {
    BarChart3, BadgePercent, Building2, Database, Megaphone, MousePointerClick, PanelLeftClose, PanelLeftOpen,
    PencilLine, ShieldCheck, Star, UserPlus, Users, Zap
} from 'lucide-react';
import '../../../../modules/shared/theme/theme.scss';
import './admin-theme.scss';

interface Props {
    UserReducer: any;
}

// Remembers whether the sidebar was collapsed (this browser only).
const COLLAPSED_KEY = 'cc_admin_sidebar_collapsed';

interface NavItem {
    label: string;
    icon: React.ReactNode;
    /** Where it goes (a sub-page keeps its own URL so a reload stays there). */
    to: string;
    /** Every section path that lights this item up. */
    paths: string[];
    /** Shown in the desktop sidebar / the phone tab row (the two used to differ, kept as is). */
    desktop: boolean;
    phone: boolean;
}

/**
 * The admin dashboard's frame: a sidebar on wider screens and a scrolling tab
 * row on phones, the section's page beside / under it. Every page inside gets
 * the brand flow's look from ./admin-theme.scss (html.brd-admin-route, set in App.tsx).
 */
const AdminDashboard: React.FC<Props> = (props: Props) => {
    const location = useLocation();
    const user = props.UserReducer;
    const isAdmin = [1, 2].includes(user?.user_role_id);
    const [collapsed, setCollapsed] = useState(() => {
        try { return localStorage.getItem(COLLAPSED_KEY) === '1'; } catch { return false; }
    });
    const toggleCollapsed = () => setCollapsed((c) => {
        try { localStorage.setItem(COLLAPSED_KEY, c ? '0' : '1'); } catch { /* storage blocked: just don't remember */ }
        return !c;
    });
    const on = (path: string) => location.pathname.includes(path);

    const items: NavItem[] = [
        { label: 'Sign Up', icon: <UserPlus size={18} />, to: on('brand-signups') ? 'brand-signups' : 'influencer-signups', paths: ['influencer-signups', 'brand-signups'], desktop: true, phone: true },
        { label: 'UserBase', icon: <Users size={18} />, to: on('userbrief') ? 'userbrief' : 'userbase', paths: ['userbase', 'userbrief'], desktop: true, phone: true },
        // OLD: to={location.pathname.includes('talent-verification') ? 'talent-verification' : 'campaign-verification'} (Talent verification is gone)
        { label: 'Verifications', icon: <ShieldCheck size={18} />, to: 'campaign-verification', paths: ['campaign-verification'], desktop: true, phone: true },
        { label: 'Platform', icon: <Megaphone size={18} />, to: on('disconnect-feedbacks') ? 'disconnect-feedbacks' : 'campaign-details', paths: ['campaign-details', 'disconnect-feedbacks'], desktop: true, phone: true },
        { label: 'Influencer Database', icon: <Database size={18} />, to: 'influencer-db', paths: ['influencer-db'], desktop: true, phone: user?.view_influencer_db == 'True' },
        { label: 'Crud', icon: <PencilLine size={18} />, to: 'crud', paths: ['crud'], desktop: isAdmin, phone: user?.create_user == 'True' },
        { label: 'Agency Brands', icon: <Building2 size={18} />, to: 'agency-brands', paths: ['agency-brands'], desktop: isAdmin, phone: isAdmin },
        { label: 'Zap Flow Tracking', icon: <Zap size={18} />, to: 'zap-flow-tracking', paths: ['zap-flow-tracking'], desktop: isAdmin, phone: isAdmin },
        { label: 'Pixel Tracking', icon: <MousePointerClick size={18} />, to: 'pixel-tracking', paths: ['pixel-tracking'], desktop: isAdmin, phone: isAdmin },
        { label: 'FOC Zap Privilege', icon: <Star size={18} />, to: 'foc-zap-privilege', paths: ['foc-zap-privilege'], desktop: isAdmin, phone: isAdmin },
        { label: 'Special Coupons', icon: <BadgePercent size={18} />, to: 'special-coupons', paths: ['special-coupons'], desktop: isAdmin, phone: isAdmin },
        { label: 'Publisher', icon: <BarChart3 size={18} />, to: 'publisher', paths: ['publisher'], desktop: isAdmin || user?.view_publisher_manager === 'True', phone: isAdmin || user?.view_publisher_manager === 'True' },
    ];
    const isActive = (item: NavItem) => item.paths.some(on);

    return (
        <div className='brd brd-admin-shell'>
            {/* Phones: the sections as a scrolling tab row */}
            <nav className='brd-admin-shell__tabs brd-chip-scroll' aria-label='Admin sections'>
                {items.filter((i) => i.phone).map((item) => (
                    <NavLink key={item.label} to={item.to} className={() => `brd-chip${isActive(item) ? ' brd-chip--on' : ''}`}>
                        {item.label}
                    </NavLink>
                ))}
            </nav>

            <aside className={`brd-admin-shell__side${collapsed ? ' brd-admin-shell__side--collapsed' : ''}`} aria-label='Admin sections'>
                <div className='brd-admin-shell__side-head'>
                    <p className='brd-admin-shell__side-title'>Admin</p>
                    <button type='button' className='brd-icon-btn brd-admin-shell__toggle' onClick={toggleCollapsed}
                        aria-label={collapsed ? 'Open sidebar' : 'Close sidebar'} aria-expanded={!collapsed}
                        title={collapsed ? 'Open sidebar' : 'Close sidebar'}>
                        {collapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
                    </button>
                </div>
                <ul>
                    {items.filter((i) => i.desktop).map((item) => (
                        <li key={item.label}>
                            <NavLink to={item.to} title={collapsed ? item.label : undefined} aria-label={item.label}
                                className={() => `brd-admin-shell__link${isActive(item) ? ' brd-admin-shell__link--on' : ''}`}>
                                {item.icon} <span className='brd-admin-shell__label'>{item.label}</span>
                            </NavLink>
                        </li>
                    ))}
                </ul>
            </aside>

            <main className='brd-admin-shell__main'>
                <Outlet />
            </main>
        </div>
    );
};
const mapStateToProps = (state: any) => {
    return {
        UserReducer: state.UserReducer.UserData,
    };
};

const connectToRedux = connect(
    mapStateToProps
)(AdminDashboard);

export { connectToRedux as AdminDashboard };


